import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Subject,
  Subscription,
  interval,
  takeUntil,
  of,
  Observable,
  forkJoin,
} from 'rxjs';
import {
  PortWithState,
  PortState,
  UpdatePortDto,
  CreatePortDto,
  PortType,
  OperatorPort,
} from '../../models/port.model';
import {
  CreateRelayDto,
  Relay,
  RelayDetails,
  UpdateRelayDto,
} from '../../models/relay.model';
import { RelayAccessItem } from '../../models/relay-access.model';
import { RelayService } from '../../services/relay.service';
import { PortService } from '../../services/port.service';
import { PortStateService } from '../../services/port-state.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { TreeNode } from '../tree/tree';
import { ModalService } from '../../services/modal.service';
import {
  RelayFormModalComponent,
  RelayFormResult,
} from './relay-form-modal/relay-form-modal';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal';
import {
  PortFormModalComponent,
  PortFormResult,
} from './port-form-modal/port-form-modal';
import { PortDetailsStubComponent } from './port-details-stub/port-details-stub';

export type DashboardCardType = 'ports' | 'sensors' | 'history';

export interface DashboardCardConfig {
  id: DashboardCardType;
  title: string;
}

export type HistoryTab = 'states' | 'events';

const HISTORY_PAGE_SIZE = 20;
const MOBILE_BREAKPOINT = 900;
const POLLING_INTERVAL_MS = 30_000;
const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE = 25;

const SAVE_ORDER_TO_SERVER = true;

@Component({
  selector: 'app-relay-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './relay-dashboard.html',
  styleUrl: './relay-dashboard.css',
})
export class RelayDashboardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() node: TreeNode | null = null;

  @ViewChild('historySentinel') historySentinel?: ElementRef<HTMLElement>;

  relays: Relay[] = [];
  relaysLoading = false;
  selectedRelay: Relay | null = null;

  ports: PortWithState[] = [];
  portsLoading = false;

  selectedPort: PortWithState | null = null;

  historyItems: PortState[] = [];
  historyLoading = false;
  historyHasMore = true;
  historyInitialized = false;

  historyTab: HistoryTab = 'states';

  isEditMode = false;

  draggedRelay: Relay | null = null;
  relayDragTarget: Relay | null = null;
  relayDragPosition: 'top' | 'bottom' | null = null;

  relayTouchDragging: Relay | null = null;

  draggedPort: PortWithState | null = null;
  portDragTarget: PortWithState | null = null;
  portDragPosition: 'top' | 'bottom' | null = null;

  portTouchDragging: PortWithState | null = null;

  get draggableCards(): DashboardCardConfig[] {
    const cards: DashboardCardConfig[] = [
      { id: 'ports', title: 'Порты' },
      { id: 'sensors', title: 'Датчики' },
      { id: 'history', title: 'История состояний' },
    ];

    if (this.isOperator) {
      return cards.filter(c => c.id !== 'sensors');
    }

    return cards;
  }

  private collapsedCardIds = new Set<string>(['ports', 'sensors', 'history']);
  private isMobileLayout = false;

  private relayLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private relayTouchStartX = 0;
  private relayTouchStartY = 0;

  private portLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private portTouchStartX = 0;
  private portTouchStartY = 0;

  private historySubscription: Subscription | null = null;
  private relaysSubscription: Subscription | null = null;
  private detailsSubscription: Subscription | null = null;
  private pollingSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();

  private sentinelObserver: IntersectionObserver | null = null;

  constructor(
    private relayService: RelayService,
    private portService: PortService,
    private portStateService: PortStateService,
    private toastService: ToastService,
    private modalService: ModalService,
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  get canEdit(): boolean {
    return this.authService.isAdmin();
  }

  get isOperator(): boolean {
    return this.authService.isOperator() && !this.authService.isAdmin();
  }

  get canSeeStatesTab(): boolean {
    return this.authService.isAdmin();
  }

  get canSeeEventsTab(): boolean {
    return this.authService.isOperator();
  }

  get showHistoryTabs(): boolean {
    return this.canSeeStatesTab && this.canSeeEventsTab;
  }

  get availableHistoryTabs(): HistoryTab[] {
    const tabs: HistoryTab[] = [];
    if (this.canSeeStatesTab) tabs.push('states');
    if (this.canSeeEventsTab) tabs.push('events');
    return tabs;
  }

  get relayPorts(): PortWithState[] {
    return this.ports.filter((p) => p.type === 'RELAY');
  }

  get sensorPorts(): PortWithState[] {
    return this.ports.filter((p) => p.type === 'SENSOR');
  }

  ngOnInit(): void {
    this.updateLayoutMode();
    this.historyTab = this.resolveDefaultHistoryTab();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['node']) return;

    if (!this.node) {
      this.resetAll();
      return;
    }

    this.isEditMode = false;
    this.resetRelayDrag();
    this.resetPortDrag();

    if (this.isOperator) {
      this.loadOperatorRelays(this.node);
    } else {
      this.loadRelays(this.node.id);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.historySubscription?.unsubscribe();
    this.relaysSubscription?.unsubscribe();
    this.detailsSubscription?.unsubscribe();
    this.stopPolling();
    this.teardownSentinelObserver();
    this.clearRelayLongPressTimer();
    this.clearPortLongPressTimer();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateLayoutMode();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.refreshCurrentStates();
    }
  }

  isCollapsed(cardId: string): boolean {
    return this.isMobileLayout && this.collapsedCardIds.has(cardId);
  }

  toggleCard(cardId: string): void {
    if (!this.isMobileLayout) return;

    if (this.collapsedCardIds.has(cardId)) {
      this.collapsedCardIds.delete(cardId);
    } else {
      this.collapsedCardIds.add(cardId);
    }

    this.cdr.detectChanges();
  }

  selectHistoryTab(tab: HistoryTab): void {
    if (this.historyTab === tab) return;
    if (!this.availableHistoryTabs.includes(tab)) return;

    this.historyTab = tab;
    this.resetHistory();

    this.cdr.detectChanges();
  }

  openPortDetailsStub(port: PortWithState): void {
    this.modalService.open({
      component: PortDetailsStubComponent,
      title: port.name,
      inputs: { port },
      onClose: () => {
        this.cdr.detectChanges();
        this.refreshCurrentStates();
      },
    });
  }

  toggleEditMode(): void {
    if (!this.canEdit) return;

    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      this.resetRelayDrag();
      this.resetPortDrag();
    }

    this.cdr.detectChanges();
  }

  selectRelay(relay: Relay): void {
    const isSameRelay = this.selectedRelay?.id === relay.id;

    this.selectedRelay = relay;

    if (isSameRelay && !this.portsLoading && this.ports.length > 0) {
      return;
    }

    this.ports = [];
    this.selectedPort = null;
    this.resetHistory();
    this.stopPolling();

    this.loadRelayDetails(relay.id);
  }

  onCreateRelay(): void {
    if (!this.canEdit || !this.isEditMode || !this.node) return;

    const facilityId = this.node.id;

    this.modalService.open({
      component: RelayFormModalComponent,
      title: 'Новое реле',
      inputs: {},
      onClose: (result?: RelayFormResult) => {
        if (!result) return;

        const dto: CreateRelayDto = {
          facilityId,
          name: result.name,
          ipAddress: result.ipAddress,
          port: result.port,
          password: result.password,
          pollingIntervalMs: result.pollingIntervalMs,
        };

        this.relayService
          .createRelay(dto)
          .pipe(
            switchMap((created) =>
              this.syncRelaysAfterMutation((data) =>
                data.some((r) => r.id === created.id),
              ),
            ),
            catchError(() => {
              this.toastService.error('Не удалось создать реле');
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe((data) => {
            if (data) {
              this.toastService.success(`Реле «${result.name}» создано`);
            }
          });
      },
    });
  }

  onEditRelay(relay: Relay, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canEdit || !this.isEditMode) return;

    this.modalService.open({
      component: RelayFormModalComponent,
      title: `Редактирование: ${relay.name}`,
      inputs: { relay },
      onClose: (result?: RelayFormResult) => {
        if (!result) return;

        if (
          result.name === relay.name &&
          result.ipAddress === relay.ipAddress &&
          result.port === relay.port &&
          result.password === (relay.password ?? null) &&
          result.pollingIntervalMs === relay.pollingIntervalMs
        ) {
          return;
        }

        const dto: UpdateRelayDto = {
          facilityId: relay.facilityId,
          name: result.name,
          ipAddress: result.ipAddress,
          port: result.port,
          password: result.password,
          pollingIntervalMs: result.pollingIntervalMs,
          orderNum: relay.orderNum,
        };

        this.relayService
          .updateRelay(relay.id, dto)
          .pipe(
            switchMap(() =>
              this.syncRelaysAfterMutation((data) => {
                const fresh = data.find((r) => r.id === relay.id);
                return (
                  !!fresh &&
                  fresh.name === result.name &&
                  fresh.ipAddress === result.ipAddress &&
                  fresh.port === result.port &&
                  fresh.password === result.password &&
                  fresh.pollingIntervalMs === result.pollingIntervalMs
                );
              }),
            ),
            catchError(() => {
              this.toastService.error('Не удалось сохранить изменения');
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe((data) => {
            if (data) {
              this.toastService.success(`Реле «${result.name}» обновлено`);
            }
          });
      },
    });
  }

  onDeleteRelay(relay: Relay, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canEdit || !this.isEditMode) return;

    const deletedId = relay.id;
    const deletedName = relay.name;

    this.confirmAction(
      `Удалить реле «${deletedName}»? Внимание: вместе с реле будут удалены все его порты и датчики`,
      'Удалить',
      'btn-danger',
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.relayService
          .deleteRelay(deletedId)
          .pipe(
            switchMap(() =>
              this.syncRelaysAfterMutation(
                (data) => !data.some((r) => r.id === deletedId),
              ),
            ),
            catchError(() => {
              this.toastService.error('Не удалось удалить реле');
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe((data) => {
            if (data) {
              this.toastService.success(`Реле «${deletedName}» удалено`);
            }
          });
      });
  }

  onRelayDragStart(relay: Relay, event: DragEvent): void {
    if (!this.isEditMode || !this.canEdit) {
      event.preventDefault();
      return;
    }

    this.draggedRelay = relay;
    this.relayDragTarget = null;
    this.relayDragPosition = null;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', relay.id);
    }
  }

  onRelayDragOver(relay: Relay, event: DragEvent): void {
    if (!this.isEditMode || !this.canEdit) return;
    if (!this.draggedRelay) return;
    if (this.draggedRelay.id === relay.id) return;

    event.preventDefault();
    event.stopPropagation();

    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const offset = event.clientY - rect.top;

    this.relayDragPosition = offset < rect.height / 2 ? 'top' : 'bottom';
    this.relayDragTarget = relay;
  }

  onRelayDragLeave(relay: Relay, _event: DragEvent): void {
    if (this.relayDragTarget?.id === relay.id) {
      this.relayDragTarget = null;
      this.relayDragPosition = null;
    }
  }

  onRelayDrop(relay: Relay, event: DragEvent): void {
    if (!this.isEditMode || !this.canEdit) return;
    if (!this.draggedRelay || !this.relayDragPosition) return;

    event.preventDefault();
    event.stopPropagation();

    const dragged = this.draggedRelay;
    const position = this.relayDragPosition;

    this.resetRelayDrag();

    if (dragged.id === relay.id) return;

    this.commitRelayReorder(dragged, relay, position);
  }

  onRelayDragEnd(): void {
    this.resetRelayDrag();
  }

  onRelayTouchStart(relay: Relay, event: TouchEvent): void {
    if (!this.isEditMode || !this.canEdit) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.relayTouchStartX = touch.clientX;
    this.relayTouchStartY = touch.clientY;

    this.clearRelayLongPressTimer();

    this.relayLongPressTimer = setTimeout(() => {
      this.relayTouchDragging = relay;
      this.cdr.detectChanges();
      (navigator as any)?.vibrate?.(15);
    }, LONG_PRESS_MS);
  }

  onRelayTouchMove(event: TouchEvent): void {
    if (!this.relayLongPressTimer && !this.relayTouchDragging) return;

    const touch = event.touches[0];
    if (!touch) return;

    if (this.relayLongPressTimer && !this.relayTouchDragging) {
      const dx = Math.abs(touch.clientX - this.relayTouchStartX);
      const dy = Math.abs(touch.clientY - this.relayTouchStartY);

      if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
        this.clearRelayLongPressTimer();
      }
    }

    if (!this.relayTouchDragging) return;

    event.preventDefault();

    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const tr = el?.closest('[data-relay-id]') as HTMLElement | null;
    const targetId = tr?.dataset['relayId'];

    if (!targetId || targetId === this.relayTouchDragging.id) {
      this.relayDragTarget = null;
      this.relayDragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    const target = this.relays.find((r) => r.id === targetId);
    if (!target) {
      this.relayDragTarget = null;
      this.relayDragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    const rect = tr!.getBoundingClientRect();
    const offset = touch.clientY - rect.top;

    this.relayDragPosition = offset < rect.height / 2 ? 'top' : 'bottom';
    this.relayDragTarget = target;
    this.cdr.detectChanges();
  }

  onRelayTouchEnd(): void {
    this.clearRelayLongPressTimer();

    if (!this.relayTouchDragging) {
      this.relayDragTarget = null;
      this.relayDragPosition = null;
      return;
    }

    const dragged = this.relayTouchDragging;
    const target = this.relayDragTarget;
    const position = this.relayDragPosition;

    this.relayTouchDragging = null;
    this.relayDragTarget = null;
    this.relayDragPosition = null;
    this.cdr.detectChanges();

    if (!target || !position) return;
    if (dragged.id === target.id) return;

    this.commitRelayReorder(dragged, target, position);
  }

  onPortDragStart(port: PortWithState, event: DragEvent): void {
    if (!this.isEditMode || !this.canEdit) {
      event.preventDefault();
      return;
    }

    this.draggedPort = port;
    this.portDragTarget = null;
    this.portDragPosition = null;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', port.id);
    }
  }

  onPortDragOver(port: PortWithState, event: DragEvent): void {
    if (!this.isEditMode || !this.canEdit) return;
    if (!this.draggedPort) return;
    if (this.draggedPort.id === port.id) return;
    if (this.draggedPort.type !== port.type) return;

    event.preventDefault();
    event.stopPropagation();

    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const offset = event.clientY - rect.top;

    this.portDragPosition = offset < rect.height / 2 ? 'top' : 'bottom';
    this.portDragTarget = port;
  }

  onPortDragLeave(port: PortWithState, _event: DragEvent): void {
    if (this.portDragTarget?.id === port.id) {
      this.portDragTarget = null;
      this.portDragPosition = null;
    }
  }

  onPortDrop(port: PortWithState, event: DragEvent): void {
    if (!this.isEditMode || !this.canEdit) return;
    if (!this.draggedPort || !this.portDragPosition) return;
    if (this.draggedPort.type !== port.type) return;

    event.preventDefault();
    event.stopPropagation();

    const dragged = this.draggedPort;
    const position = this.portDragPosition;

    this.resetPortDrag();

    if (dragged.id === port.id) return;

    this.commitPortReorder(dragged, port, position);
  }

  onPortDragEnd(): void {
    this.resetPortDrag();
  }

  onPortTouchStart(port: PortWithState, event: TouchEvent): void {
    if (!this.isEditMode || !this.canEdit) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.portTouchStartX = touch.clientX;
    this.portTouchStartY = touch.clientY;

    this.clearPortLongPressTimer();

    this.portLongPressTimer = setTimeout(() => {
      this.portTouchDragging = port;
      this.cdr.detectChanges();
      (navigator as any)?.vibrate?.(15);
    }, LONG_PRESS_MS);
  }

  onPortTouchMove(event: TouchEvent): void {
    if (!this.portLongPressTimer && !this.portTouchDragging) return;

    const touch = event.touches[0];
    if (!touch) return;

    if (this.portLongPressTimer && !this.portTouchDragging) {
      const dx = Math.abs(touch.clientX - this.portTouchStartX);
      const dy = Math.abs(touch.clientY - this.portTouchStartY);

      if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
        this.clearPortLongPressTimer();
      }
    }

    if (!this.portTouchDragging) return;

    event.preventDefault();

    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const li = el?.closest('[data-port-id]') as HTMLElement | null;
    const targetId = li?.dataset['portId'];

    if (!targetId || targetId === this.portTouchDragging.id) {
      this.portDragTarget = null;
      this.portDragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    const target = this.ports.find((p) => p.id === targetId);
    if (!target) {
      this.portDragTarget = null;
      this.portDragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    if (this.portTouchDragging.type !== target.type) {
      this.portDragTarget = null;
      this.portDragPosition = null;
      this.cdr.detectChanges();
      return;
    }

    const rect = li!.getBoundingClientRect();
    const offset = touch.clientY - rect.top;

    this.portDragPosition = offset < rect.height / 2 ? 'top' : 'bottom';
    this.portDragTarget = target;
    this.cdr.detectChanges();
  }

  onPortTouchEnd(): void {
    this.clearPortLongPressTimer();

    if (!this.portTouchDragging) {
      this.portDragTarget = null;
      this.portDragPosition = null;
      return;
    }

    const dragged = this.portTouchDragging;
    const target = this.portDragTarget;
    const position = this.portDragPosition;

    this.portTouchDragging = null;
    this.portDragTarget = null;
    this.portDragPosition = null;
    this.cdr.detectChanges();

    if (!target || !position) return;
    if (dragged.id === target.id) return;

    this.commitPortReorder(dragged, target, position);
  }

  private resolveDefaultHistoryTab(): HistoryTab {
    if (this.authService.isAdmin()) return 'states';
    return 'events';
  }

  private resetRelayDrag(): void {
    this.clearRelayLongPressTimer();
    this.draggedRelay = null;
    this.relayTouchDragging = null;
    this.relayDragTarget = null;
    this.relayDragPosition = null;
    this.cdr.detectChanges();
  }

  private resetPortDrag(): void {
    this.clearPortLongPressTimer();
    this.draggedPort = null;
    this.portTouchDragging = null;
    this.portDragTarget = null;
    this.portDragPosition = null;
    this.cdr.detectChanges();
  }

  private clearRelayLongPressTimer(): void {
    if (this.relayLongPressTimer) {
      clearTimeout(this.relayLongPressTimer);
      this.relayLongPressTimer = null;
    }
  }

  private clearPortLongPressTimer(): void {
    if (this.portLongPressTimer) {
      clearTimeout(this.portLongPressTimer);
      this.portLongPressTimer = null;
    }
  }

  private commitRelayReorder(
    dragged: Relay,
    target: Relay,
    position: 'top' | 'bottom',
  ): void {
    if (dragged.id === target.id) return;

    const fromIndex = this.relays.findIndex((r) => r.id === dragged.id);
    const targetIndex = this.relays.findIndex((r) => r.id === target.id);
    if (fromIndex === -1 || targetIndex === -1) return;
    if (fromIndex === targetIndex) return;

    const without = this.relays.filter((r) => r.id !== dragged.id);
    const targetIdxInWithout = without.findIndex((r) => r.id === target.id);
    const insertAt =
      position === 'top' ? targetIdxInWithout : targetIdxInWithout + 1;
    without.splice(insertAt, 0, dragged);

    const updates: Relay[] = [];
    without.forEach((relay, index) => {
      if (relay.orderNum !== index) {
        relay.orderNum = index;
        updates.push(relay);
      }
    });

    this.relays = without;
    this.cdr.detectChanges();

    if (updates.length === 0) return;

    if (!SAVE_ORDER_TO_SERVER) {
      this.toastService.info('Порядок изменён (сохранение пока недоступно)');
      return;
    }

    const requests = updates.map((relay) => {
      const dto: UpdateRelayDto = {
        facilityId: relay.facilityId,
        name: relay.name,
        ipAddress: relay.ipAddress,
        port: relay.port,
        password: relay.password,
        pollingIntervalMs: relay.pollingIntervalMs,
        orderNum: relay.orderNum,
      };
      return this.relayService.updateRelay(relay.id, dto);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.toastService.success('Порядок реле обновлён');
        this.syncRelaysAfterMutation();
      },
      error: () => {
        this.toastService.error('Не удалось сохранить порядок');
        this.syncRelaysAfterMutation();
      },
    });
  }

  private commitPortReorder(
    dragged: PortWithState,
    target: PortWithState,
    position: 'top' | 'bottom',
  ): void {
    if (dragged.id === target.id) return;
    if (dragged.type !== target.type) return;

    const isRelay = dragged.type === 'RELAY';
    const currentList = isRelay ? this.relayPorts : this.sensorPorts;

    const fromIndex = currentList.findIndex((p) => p.id === dragged.id);
    const targetIndex = currentList.findIndex((p) => p.id === target.id);
    if (fromIndex === -1 || targetIndex === -1) return;
    if (fromIndex === targetIndex) return;

    const without = currentList.filter((p) => p.id !== dragged.id);
    const targetIdxInWithout = without.findIndex((p) => p.id === target.id);
    const insertAt =
      position === 'top' ? targetIdxInWithout : targetIdxInWithout + 1;
    without.splice(insertAt, 0, dragged);

    const updates: PortWithState[] = [];
    without.forEach((port, index) => {
      if (port.orderNum !== index) {
        port.orderNum = index;
        updates.push(port);
      }
    });

    this.ports = [
      ...without,
      ...this.ports.filter((p) => p.type !== dragged.type),
    ];

    this.cdr.detectChanges();

    if (updates.length === 0) return;

    if (!SAVE_ORDER_TO_SERVER) {
      this.toastService.info('Порядок изменён (сохранение пока недоступно)');
      return;
    }

    const relayId = this.selectedRelay?.id;
    if (!relayId) return;

    const requests = updates.map((port) => {
      const dto: UpdatePortDto = {
        relayId,
        type: port.type,
        portNumber: port.portNumber,
        name: port.name,
        active: port.active,
        orderNum: port.orderNum,
      };
      return this.portService.updatePort(port.id, dto);
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.toastService.success('Порядок обновлён');
        this.syncPortsAfterMutation(relayId);
      },
      error: () => {
        this.toastService.error('Не удалось сохранить порядок');
        this.syncPortsAfterMutation(relayId);
      },
    });
  }

  private syncRelaysAfterMutation(
    expectation?: (data: Relay[]) => boolean,
  ): Observable<Relay[] | null> {
    if (!this.node) return of(null);

    if (this.isOperator) {
      this.loadOperatorRelays(this.node);
      return of(this.relays);
    }

    const facilityId = this.node.id;
    const selectedRelayId = this.selectedRelay?.id ?? null;

    return this.relayService.getRelays(facilityId).pipe(
      map((data) => {
        this.relays = data;

        if (expectation && !expectation(data)) {
          this.showAlertModal(
            'Данные на сервере отличаются от ожидаемых. ' +
            'Список реле приведён к состоянию сервера.',
          );
        }

        if (selectedRelayId) {
          const stillExists = data.find((r) => r.id === selectedRelayId);

          if (stillExists && this.selectedRelay?.id === selectedRelayId) {
            this.selectedRelay = stillExists;
          } else if (stillExists) {
            this.selectRelay(stillExists);
          } else if (data.length > 0) {
            this.selectRelay(data[0]);
          } else {
            this.selectedRelay = null;
            this.ports = [];
            this.selectedPort = null;
            this.resetHistory();
          }
        } else if (data.length > 0) {
          this.selectRelay(data[0]);
        } else {
          this.selectedRelay = null;
          this.ports = [];
          this.selectedPort = null;
          this.resetHistory();
        }

        this.cdr.detectChanges();
        return data;
      }),
      catchError(() => {
        this.showAlertModal(
          'Не удалось получить актуальные данные с сервера. ' +
          'Попробуйте обновить страницу.',
        );
        return of(null);
      }),
    );
  }

  private syncPortsAfterMutation(
    relayId: string,
    expectation?: (data: PortWithState[]) => boolean,
  ): Observable<PortWithState[] | null> {
    const selectedPortId = this.selectedPort?.id ?? null;

    const request$ = this.isOperator
      ? this.relayService.getOperatorRelayPorts(relayId).pipe(
        map((operatorPorts) => ({
          ports: operatorPorts.map((p) => this.mapOperatorPort(p)),
        }) as RelayDetails),
      )
      : this.relayService.getRelayDetails(relayId);

    return request$.pipe(
      map((details) => {
        if (this.selectedRelay?.id !== relayId) return null;

        const ports = details.ports ?? [];
        this.ports = ports;

        if (expectation && !expectation(ports)) {
          this.showAlertModal(
            'Данные на сервере отличаются от ожидаемых. ' +
            'Список портов приведён к состоянию сервера.',
          );
        }

        if (selectedPortId) {
          const stillExists = ports.find((p) => p.id === selectedPortId);
          if (stillExists) {
            this.selectedPort = stillExists;
          } else {
            const firstRelay = ports.find((p) => p.type === 'RELAY');
            this.selectedPort = firstRelay ?? ports[0] ?? null;
            this.resetHistory();
          }
        } else if (ports.length > 0) {
          const firstRelay = ports.find((p) => p.type === 'RELAY');
          this.selectedPort = firstRelay ?? ports[0];
          this.resetHistory();
        }

        this.cdr.detectChanges();
        return ports;
      }),
      catchError(() => {
        this.showAlertModal(
          'Не удалось получить актуальные данные с сервера. ' +
          'Попробуйте обновить страницу.',
        );
        return of(null);
      }),
    );
  }

  private confirmAction(
    message: string,
    confirmText: string,
    buttonClass = 'btn-primary',
  ): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.modalService.open({
        component: ConfirmModalComponent,
        title: 'Подтверждение действия',
        inputs: {
          message,
          confirmText,
          confirmButtonClass: buttonClass,
        },
        onClose: (result) => {
          subscriber.next(result === true);
          subscriber.complete();
        },
      });
    });
  }

  private showAlertModal(message: string): void {
    this.modalService.open({
      component: ConfirmModalComponent,
      title: 'Внимание',
      inputs: {
        message,
        confirmText: 'Понятно',
        cancelText: 'Закрыть',
        confirmButtonClass: 'btn-primary',
      },
      onClose: () => {},
    });
  }

  selectPort(port: PortWithState): void {
    if (this.selectedPort?.id === port.id) return;

    this.selectedPort = port;
    this.resetHistory();
  }

  isSelectedPort(port: PortWithState): boolean {
    return this.selectedPort?.id === port.id;
  }

  onTogglePortActive(port: PortWithState, active: boolean): void {
    if (!this.canEdit || !this.isEditMode) return;

    const relayId = this.selectedRelay?.id;
    if (!relayId) return;

    const previousActive = port.active;
    port.active = active;
    this.cdr.detectChanges();

    const dto: UpdatePortDto = {
      relayId,
      type: port.type,
      portNumber: port.portNumber,
      name: port.name,
      active,
      orderNum: port.orderNum,
    };

    this.portService
      .updatePort(port.id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.success(
            active
              ? `Порт «${port.name}» активирован`
              : `Порт «${port.name}» деактивирован`,
          );
        },
        error: () => {
          port.active = previousActive;
          this.toastService.error('Не удалось изменить активность порта');
          this.cdr.detectChanges();
        },
      });
  }

  onCreatePort(): void {
    if (!this.canEdit || !this.isEditMode || !this.selectedRelay) return;
    this.openPortForm('RELAY');
  }

  onCreateSensor(): void {
    if (!this.canEdit || !this.isEditMode || !this.selectedRelay) return;
    this.openPortForm('SENSOR');
  }

  onEditPort(port: PortWithState, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canEdit || !this.isEditMode || !this.selectedRelay) return;

    const relayId = this.selectedRelay.id;

    this.modalService.open({
      component: PortFormModalComponent,
      title: `Редактирование: ${port.name}`,
      inputs: { port, defaultType: port.type },
      onClose: (result?: PortFormResult) => {
        if (!result) return;

        if (
          result.name === port.name &&
          result.portNumber === port.portNumber &&
          result.active === port.active &&
          result.type === port.type
        ) {
          return;
        }

        const dto: UpdatePortDto = {
          relayId,
          type: port.type,
          portNumber: result.portNumber,
          name: result.name,
          active: result.active,
          orderNum: port.orderNum,
        };

        this.portService
          .updatePort(port.id, dto)
          .pipe(
            switchMap(() =>
              this.syncPortsAfterMutation(relayId, (data) => {
                const fresh = data.find((p) => p.id === port.id);
                return (
                  !!fresh &&
                  fresh.name === result.name &&
                  fresh.portNumber === result.portNumber &&
                  fresh.active === result.active
                );
              }),
            ),
            catchError(() => {
              this.toastService.error('Не удалось сохранить порт');
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe((data) => {
            if (data) {
              this.toastService.success(`Порт «${result.name}» обновлён`);
            }
          });
      },
    });
  }

  onDeletePort(port: PortWithState, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canEdit || !this.isEditMode || !this.selectedRelay) return;

    const relayId = this.selectedRelay.id;
    const deletedId = port.id;
    const deletedName = port.name;

    this.confirmAction(`Удалить «${deletedName}»?`, 'Удалить', 'btn-danger')
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.portService
          .deletePort(deletedId)
          .pipe(
            switchMap(() =>
              this.syncPortsAfterMutation(
                relayId,
                (data) => !data.some((p) => p.id === deletedId),
              ),
            ),
            catchError(() => {
              this.toastService.error('Не удалось удалить порт');
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe((data) => {
            if (data) {
              this.toastService.success(`«${deletedName}» удалено`);
            }
          });
      });
  }

  private openPortForm(defaultType: PortType): void {
    const relayId = this.selectedRelay?.id;
    if (!relayId) return;

    this.modalService.open({
      component: PortFormModalComponent,
      title: defaultType === 'RELAY' ? 'Новый порт' : 'Новый датчик',
      inputs: { defaultType },
      onClose: (result?: PortFormResult) => {
        if (!result) return;

        const dto: CreatePortDto = {
          relayId,
          type: result.type,
          portNumber: result.portNumber,
          name: result.name,
          active: result.active,
        };

        this.portService
          .createPort(dto)
          .pipe(
            switchMap((created) =>
              this.syncPortsAfterMutation(relayId, (data) =>
                data.some((p) => p.id === created.id),
              ),
            ),
            catchError(() => {
              this.toastService.error('Не удалось создать порт');
              return of(null);
            }),
            takeUntil(this.destroy$),
          )
          .subscribe((data) => {
            if (data) {
              this.toastService.success(`«${result.name}» создано`);
            }
          });
      },
    });
  }

  onHistoryScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) return;

    const threshold = 120;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    if (distanceFromBottom < threshold) {
      this.loadNextHistoryPage();
    }
  }

  getStateIndicatorClass(state: string | null | undefined): string {
    if (!state) return '';
    switch (state) {
      case 'ON':
        return 'is-normal';
      case 'OFF':
      case 'ERROR':
        return 'is-danger';
      default:
        return 'is-warning';
    }
  }

  getStateLabel(state: string | null | undefined): string {
    if (!state) return '—';

    const s = state.trim().toLowerCase();

    if (s === 'on' || s === 'норма' || s === 'normal' || s === 'ok') {
      return 'Вкл';
    }
    if (s === 'off' || s.includes('перезагрузк') || s.includes('reboot')) {
      return 'Выкл';
    }
    return 'Ошибка';
  }

  isErrorState(state: string | null | undefined): boolean {
    if (!state) return false;
    return state.trim().toLowerCase() === 'error';
  }

  private resetAll(): void {
    this.relays = [];
    this.selectedRelay = null;
    this.ports = [];
    this.selectedPort = null;
    this.isEditMode = false;
    this.resetRelayDrag();
    this.resetPortDrag();
    this.resetHistory();
    this.stopPolling();
    this.teardownSentinelObserver();
  }

  private updateLayoutMode(): void {
    const wasMobile = this.isMobileLayout;
    this.isMobileLayout = window.innerWidth < MOBILE_BREAKPOINT;

    if (!wasMobile && this.isMobileLayout) {
      this.collapsedCardIds = new Set<string>(['ports', 'sensors', 'history']);
    }

    this.cdr.detectChanges();
  }

  private loadRelays(facilityId: string): void {
    this.relaysLoading = true;
    this.relays = [];
    this.selectedRelay = null;
    this.ports = [];
    this.selectedPort = null;
    this.resetHistory();
    this.stopPolling();
    this.teardownSentinelObserver();
    this.cdr.detectChanges();

    this.relaysSubscription?.unsubscribe();

    this.relaysSubscription = this.relayService
      .getRelays(facilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.relays = data;
          this.relaysLoading = false;

          if (data.length > 0) {
            this.selectRelay(data[0]);
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.relaysLoading = false;
          this.toastService.error('Не удалось загрузить список реле');
          this.cdr.detectChanges();
        },
      });
  }

  private loadOperatorRelays(node: TreeNode): void {
    this.relaysLoading = false;

    this.relaysSubscription?.unsubscribe();
    this.relaysSubscription = null;

    const accessRelays = node.accessRelays ?? [];
    this.relays = accessRelays.map((r) => this.mapAccessToRelay(r));

    this.selectedRelay = null;
    this.ports = [];
    this.selectedPort = null;
    this.resetHistory();
    this.stopPolling();
    this.teardownSentinelObserver();
    this.cdr.detectChanges();

    if (this.relays.length > 0) {
      this.selectRelay(this.relays[0]);
    }
  }

  private mapAccessToRelay(item: RelayAccessItem): Relay {
    return {
      id: item.id,
      facilityId: item.facilityId,
      name: item.name,
      ipAddress: '',
      port: 0,
      password: null,
      pollingIntervalMs: 0,
      orderNum: 0,
      createdAt: '',
      updatedAt: '',
    };
  }

  private mapOperatorPort(port: OperatorPort): PortWithState {
    let state: PortState | null = null;

    if (port.activeSession) {
      state = {
        id: port.activeSession.id,
        relayPortId: port.id,
        state: port.activeSession.type === 'WORKING' ? 'ON' : 'OFF',
        recordedAt: port.activeSession.startedAt,
      };
    }

    return {
      id: port.id,
      relayId: port.relayId,
      type: 'RELAY',
      portNumber: port.portNumber,
      name: port.name,
      active: port.active,
      orderNum: port.orderNum,
      currentState: state,
      activeSession: port.activeSession,
    };
  }

  private loadRelayDetails(relayId: string): void {
    this.portsLoading = true;
    this.cdr.detectChanges();

    this.detailsSubscription?.unsubscribe();

    const details$ = this.isOperator
      ? this.relayService.getOperatorRelayPorts(relayId).pipe(
        map((operatorPorts) => ({
          ports: operatorPorts.map((p) => this.mapOperatorPort(p)),
        }) as RelayDetails),
      )
      : this.relayService.getRelayDetails(relayId);

    this.detailsSubscription = details$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.selectedRelay?.id === relayId) {
            this.portsLoading = false;
            this.cdr.detectChanges();
          }
        }),
      )
      .subscribe({
        next: (details: RelayDetails) => {
          if (this.selectedRelay?.id !== relayId) return;

          this.ports = details.ports ?? [];

          const firstRelayPort = this.ports.find((p) => p.type === 'RELAY');
          const firstAny = this.ports[0] ?? null;
          this.selectedPort = firstRelayPort ?? firstAny;

          this.resetHistory();
          this.startPolling(relayId);
          this.cdr.detectChanges();
        },
        error: () => {
          if (this.selectedRelay?.id !== relayId) return;

          this.toastService.error('Не удалось загрузить данные реле');
          this.cdr.detectChanges();
        },
      });
  }

  private startPolling(relayId: string): void {
    this.stopPolling();

    this.pollingSubscription = interval(POLLING_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState !== 'visible') return;
        if (this.selectedRelay?.id !== relayId) return;
        this.refreshCurrentStates();
      });
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  private refreshCurrentStates(): void {
    const relayId = this.selectedRelay?.id;
    if (!relayId) return;
    if (this.portsLoading) return;

    const request$ = this.isOperator
      ? this.relayService.getOperatorRelayPorts(relayId).pipe(
        map((operatorPorts) => ({
          ports: operatorPorts.map((p) => this.mapOperatorPort(p)),
        }) as RelayDetails),
      )
      : this.relayService.getRelayDetails(relayId);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (details: RelayDetails) => {
          if (this.selectedRelay?.id !== relayId) return;

          const map = new Map(details.ports.map((p) => [p.id, p]));
          this.ports = this.ports.map((p) => {
            const fresh = map.get(p.id);
            return fresh
              ? {
                ...p,
                currentState: fresh.currentState,
                active: fresh.active,
                activeSession: fresh.activeSession
              }
              : p;
          });

          if (this.selectedPort) {
            const updated = map.get(this.selectedPort.id);
            if (updated) {
              this.selectedPort = {
                ...this.selectedPort,
                currentState: updated.currentState,
                active: updated.active,
                activeSession: updated.activeSession,
              };
            }
          }

          this.cdr.detectChanges();
        },
        error: () => {},
      });
  }

  private resetHistory(): void {
    this.historySubscription?.unsubscribe();
    this.historySubscription = null;

    this.historyItems = [];
    this.historyHasMore = true;
    this.historyLoading = false;
    this.historyInitialized = false;

    this.teardownSentinelObserver();

    if (this.historyTab !== 'states') {
      this.cdr.detectChanges();
      return;
    }

    if (!this.selectedPort) {
      this.cdr.detectChanges();
      return;
    }

    this.loadNextHistoryPage();
  }

  private loadNextHistoryPage(): void {
    if (this.historyTab !== 'states') return;
    if (this.historyLoading || !this.historyHasMore) return;

    const portId = this.selectedPort?.id;
    if (!portId) return;

    this.historyLoading = true;

    const before =
      this.historyItems.length > 0
        ? this.historyItems[this.historyItems.length - 1].recordedAt
        : null;

    const portIdAtRequest = portId;

    this.historySubscription?.unsubscribe();

    this.historySubscription = this.portStateService
      .getHistory(portIdAtRequest, HISTORY_PAGE_SIZE, before)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          if (this.selectedPort?.id !== portIdAtRequest) return;

          this.historyItems = [...this.historyItems, ...items];
          this.historyHasMore = items.length === HISTORY_PAGE_SIZE;
          this.historyLoading = false;
          this.historyInitialized = true;
          this.cdr.detectChanges();

          this.refreshSentinelObserver();
        },
        error: () => {
          this.historyLoading = false;
          this.historyInitialized = true;
          this.cdr.detectChanges();
        },
      });
  }

  private refreshSentinelObserver(): void {
    if (typeof IntersectionObserver === 'undefined') return;

    const sentinel = this.historySentinel?.nativeElement;
    if (!sentinel) return;

    if (this.sentinelObserver) {
      this.sentinelObserver.disconnect();
    }

    this.sentinelObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.loadNextHistoryPage();
          }
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0,
      },
    );

    this.sentinelObserver.observe(sentinel);
  }

  private teardownSentinelObserver(): void {
    this.sentinelObserver?.disconnect();
    this.sentinelObserver = null;
  }
}
